import { WIDGET_PATH } from '@/features/widget-design-studio/studio-targets.types';
import { normalizeSearch } from '@/lib/search-haystack';
import { paneLabelKey } from '@/lib/setting-anchor-routes';
import { SETTING_ANCHORS, SETTING_KEYS, settingAnchorHref } from '@/lib/setting-anchors';
import { isControlSearchable, isSearchable } from '@/lib/settings-search-gate';

import type { SidebarItem } from '@/components/Sidebar/Sidebar';
import type { SettingKey } from '@/lib/setting-anchors';
import type { SettingsSearchGateContext } from '@/lib/settings-search-gate';

/**
 * The ONE index both search surfaces read.
 *
 * The top-bar field and the ⌘K palette answer the same question — "where is the
 * option that does X?" — and they used to answer it from two different places:
 * the palette held its own reachability check and its own substring filter over
 * labels alone. Two implementations of one question drift, and the drift is
 * silent in the direction that matters, because a surface offering FEWER results
 * looks identical to a surface with nothing to offer.
 *
 * An entry is one of three things, and the order below is the order a reader
 * gets them in:
 *
 *  - a **setting** — one card, the base unit;
 *  - a **control** — one option inside a card that mixes several, so somebody
 *    who typed "language" lands on the language rows rather than on the card
 *    that happens to contain them;
 *  - a **page** — a dashboard destination by name, from the same
 *    already-gated rows the sidebar renders, so the search can never offer a
 *    surface the sidebar hides.
 *
 * Each carries a **haystack**: its localized label, its breadcrumb and its
 * keyword bag, normalized once. Built per locale rather than per keystroke,
 * because none of those changes while somebody types.
 */

export type SettingsSearchKind = 'setting' | 'control' | 'page';

export interface SettingsSearchEntry {
  /** Stable id — the React key and the `aria-activedescendant` target. */
  readonly id: string;
  readonly kind: SettingsSearchKind;
  /** Localized name, as the thing it opens renders it. */
  readonly label: string;
  /** Localized "Page › Tab" trail, so a result says where it lives. */
  readonly breadcrumb: readonly string[];
  /** The in-app link — copyable, and what a page entry navigates to. */
  readonly href: string;
  /** Normalized label + breadcrumb + keyword bag. */
  readonly haystack: string;
  /** Set on a setting or control: how to jump and what to land on. */
  readonly settingKey?: SettingKey;
  readonly controlId?: string;
}

/**
 * The page a route belongs to, by i18n key.
 *
 * Derived from the sidebar rows the caller already holds rather than from a
 * second table, so a renamed nav row renames the breadcrumb with it. TWO routes
 * are additions, both for the same reason — they carry no sidebar row, so the
 * lookup below finds nothing and their settings would trail off into silence:
 *
 *  - `/account` lives in the user menu;
 *  - `/widget` is reached only from a design profile's Edit
 *    (`sidebar-definitions.ts` says so where the row would otherwise be), which
 *    is why every `/widget` result read as a bare name until this map existed.
 */
const UNLISTED_PAGE_LABEL_KEY: Readonly<Record<string, string>> = {
  '/account': 'account.title',
  [WIDGET_PATH]: 'sidebar.designProfiles',
};

export interface SettingsSearchIndexInput {
  /** Message resolver — `useT().t`. */
  readonly t: (key: string) => string;
  /** The localized, already-gated nav rows the sidebar renders. */
  readonly sidebarItems: readonly SidebarItem[];
  readonly gate: SettingsSearchGateContext;
}

/** Localized page name for a route, or `undefined` when nothing names it. */
function pageLabel(
  path: string,
  sidebarItems: readonly SidebarItem[],
  t: (key: string) => string,
): string | undefined {
  const unlisted = UNLISTED_PAGE_LABEL_KEY[path];
  if (unlisted !== undefined) return t(unlisted);
  return sidebarItems.find((item) => item.to === path)?.label;
}

/** "Workspace › Data & privacy" — the page, then the tab it hides behind. */
function breadcrumbFor(
  key: SettingKey,
  sidebarItems: readonly SidebarItem[],
  t: (key: string) => string,
): readonly string[] {
  const { path, pane } = SETTING_ANCHORS[key];
  const trail: string[] = [];
  const page = pageLabel(path, sidebarItems, t);
  if (page !== undefined) trail.push(page);
  // `undefined` here is a DECISION on the routes that index one anchor per pane
  // (`/connections`, `/team`, `/widget`): the anchor's own label already is the
  // pane's name, so a segment would say the same word twice. See
  // `SettingAnchorRoute.panes`.
  const paneKey = paneLabelKey(path, pane);
  if (paneKey !== undefined) trail.push(t(paneKey));
  return trail;
}

function haystackOf(parts: readonly (string | undefined)[]): string {
  return parts
    .filter((part): part is string => part !== undefined && part.length > 0)
    .map(normalizeSearch)
    .join(' ');
}

/**
 * Build the index for one locale, mode and role.
 *
 * Settings first, then their controls beside them, then pages — the operator's
 * own order, since "which option does this?" is the question the surface exists
 * for and "which page is that?" is the fallback.
 */
export function buildSettingsSearchIndex({
  t,
  sidebarItems,
  gate,
}: SettingsSearchIndexInput): readonly SettingsSearchEntry[] {
  const entries: SettingsSearchEntry[] = [];
  // Two group words that are SEARCHABLE without being rendered.
  //
  // The palette used to show "Settings" as a group label on every per-setting
  // row and matched on it, so typing "settings" surfaced the whole set at once.
  // The breadcrumb replaced that label with something more useful — the page and
  // tab — and would have taken the matching with it. A row saying
  // "Workspace › Data & privacy › Settings" is noise; the word belongs in the
  // haystack, not on the row. Same for the pages group.
  const settingsGroup = t('palette.group.settings');
  const pagesGroup = t('settingsSearch.pagesGroup');

  for (const key of SETTING_KEYS) {
    const anchor = SETTING_ANCHORS[key];
    if (!isSearchable(anchor, gate)) continue;
    const breadcrumb = breadcrumbFor(key, sidebarItems, t);
    const label = t(anchor.labelKey);
    entries.push({
      id: `setting:${key}`,
      kind: 'setting',
      label,
      breadcrumb,
      href: settingAnchorHref(key),
      haystack: haystackOf([label, ...breadcrumb, t(anchor.keywordsKey), settingsGroup]),
      settingKey: key,
    });

    for (const control of anchor.controls ?? []) {
      if (!isControlSearchable(control, gate)) continue;
      const controlLabel = t(control.labelKey);
      // The card's own name joins the trail: a control is only meaningful under
      // the card it belongs to, and "Language" alone says nothing about where.
      const controlTrail = [...breadcrumb, label];
      entries.push({
        id: `control:${key}:${control.id}`,
        kind: 'control',
        label: controlLabel,
        breadcrumb: controlTrail,
        href: settingAnchorHref(key, control.id),
        haystack: haystackOf([
          controlLabel,
          ...controlTrail,
          control.keywordsKey === undefined ? undefined : t(control.keywordsKey),
          settingsGroup,
        ]),
        settingKey: key,
        controlId: control.id,
      });
    }
  }

  for (const item of sidebarItems) {
    // No gate call: these rows arrive already filtered by RBAC, the Labs flags
    // and the handoff opt-in. Re-deciding here would be a second implementation
    // of the sidebar's own rules, which is the drift this index exists to end.
    const trail = item.group === undefined ? [] : [item.group];
    entries.push({
      id: `page:${item.to}`,
      kind: 'page',
      label: item.label,
      breadcrumb: trail,
      href: item.to,
      haystack: haystackOf([item.label, ...trail, pagesGroup]),
    });
  }

  return entries;
}
